import slugify from 'slugify'

import { type PublicTenant } from '@rewardops/env'

import { type CategoryData, type CategorySummaryData } from './types/rewardops'

// Eg. getCleanCategory('Home & Garden') => 'home-and-garden'
export function getCategoryPath(
  category: Pick<CategoryData | CategorySummaryData, 'name'>,
  locale: string,
) {
  const name = category.name?.[locale]

  if (!name) return undefined

  return slugify(name, {
    lower: true,
  })
    .toLowerCase()
    .trim()
}

export function getCategoryPathDepth(category: CategoryData): number {
  const path = category.full_path ?? ''
  return (path.match(/\//g) || []).length
}

export function getCategoryByOptions(data: {
  categories: CategoryData[] | undefined
  options: {
    path: string | undefined
    locale: string
    depthLevel?: number // Using depthLevel to indicate the nesting depth
  }
}) {
  const { categories, options: categoryOptions } = data
  const { path: categoryPath, locale, depthLevel } = categoryOptions

  if (!categories || !categoryPath) return undefined

  if (!depthLevel) {
    return categories.find(
      (cat) => getCategoryPath(cat, locale) === categoryPath,
    )
  }

  return categories.find((cat) => {
    const catPath = getCategoryPath(cat, locale) ?? ''
    // Calculate the current category's depth level based on slash count
    const catDepthLevel = getCategoryPathDepth(cat)
    return catPath === categoryPath && catDepthLevel === depthLevel
  })
}

export function findRootCategory(
  tenant: PublicTenant,
  categories?: CategoryData[],
) {
  if (!categories) return undefined
  return categories.find((cat) => cat.code === tenant.roRootCategory)
}

export function findCategoryByCode(
  categories: CategoryData[],
  code: string,
): CategoryData | undefined {
  return categories.find((cat) => cat.code === code)
}

export function findSecondLevelCategories(
  tenant: PublicTenant,
  categories?: CategoryData[],
) {
  if (!categories) return []
  const rootCategory = findRootCategory(tenant, categories)
  if (!rootCategory) return []
  return rootCategory.children
}

export const findCategoryFromParentAndName = (
  name: string,
  parent: string,
  locale: string,
  categories?: CategoryData[],
) => {
  if (!categories) return undefined

  const parentPath = getCategoryPath(
    {
      name: { [locale]: parent },
    },
    locale,
  )

  const isCategory = (
    cat: CategoryData | CategorySummaryData,
    name: string,
  ) => {
    const catPath = getCategoryPath(cat, locale)
    return catPath === name || catPath === `${parentPath}/${name}`
  }

  // Find the sub/category the product page belongs to and use that code to lookup
  const parentCategory = categories.find((cat) => isCategory(cat, parent))
  return parentCategory?.children.find((cat) => isCategory(cat, name))
}
